import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import Reveal from "../components/Reveal";
import SectionLabel from "../components/SectionLabel";
import { useSite, resolveImg } from "../context/SiteContext";

const STATE_FILTERS = ["All", "Meghalaya", "Assam", "Arunachal Pradesh"];

export default function Destinations() {
  const { destinations } = useSite();
  // Allow ?state=meghalaya in the URL (used by home page state cards)
  const initialState = (() => {
    if (typeof window === "undefined") return "All";
    const q = new URLSearchParams(window.location.search).get("state");
    if (!q) return "All";
    const match = STATE_FILTERS.find((s) => s.toLowerCase() === q.toLowerCase());
    return match || "All";
  })();
  const [active, setActive] = useState(initialState);

  const filtered = useMemo(
    () => (active === "All" ? destinations : destinations.filter((d) => d.state === active)),
    [active, destinations]
  );

  return (
    <main data-testid="destinations-page" className="pt-32 pb-24">
      <section className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <SectionLabel>Destinations</SectionLabel>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-light text-balance max-w-3xl">
              Every place we travel to, in <em className="not-italic font-medium">one place</em>.
            </h1>
            <p className="text-muted-foreground max-w-md">
              {destinations.length} curated destinations across Meghalaya, Assam and Arunachal — each with images, starting prices and a detailed guide.
            </p>
          </div>
        </Reveal>

        {/* State filter */}
        <Reveal delay={0.05}>
          <div className="mt-10 flex flex-wrap gap-2" role="tablist" aria-label="State filter">
            {STATE_FILTERS.map((s) => {
              const isActive = active === s;
              return (
                <button
                  key={s}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(s)}
                  data-testid={`state-filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-4 h-10 rounded-full text-sm border transition-all ${
                    isActive
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card text-foreground/80 border-border hover:border-primary/60"
                  }`}
                >
                  {s}{" "}
                  <span className={`ml-1 text-xs ${isActive ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                    {s === "All" ? destinations.length : destinations.filter((d) => d.state === s).length}
                  </span>
                </button>
              );
            })}
          </div>
        </Reveal>

        {/* Grid */}
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((d, i) => (
            <Reveal key={d.slug} delay={(i % 6) * 0.05}>
              <article
                data-testid={`destination-card-${d.slug}`}
                className="group rounded-3xl overflow-hidden border border-border bg-card hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 h-full flex flex-col"
              >
                <Link to={`/destinations/${d.slug}`} aria-label={d.name} className="relative h-64 overflow-hidden block">
                  <img
                    src={resolveImg(d.hero)}
                    alt={d.name}
                    className="h-full w-full object-cover transition-transform duration-[1500ms] group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 flex gap-2">
                    <span className="glass rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-foreground">
                      {d.state}
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white">
                    <h2 className="font-display text-2xl md:text-3xl font-light leading-tight drop-shadow">
                      {d.name}
                    </h2>
                  </div>
                </Link>

                <div className="p-6 flex flex-col flex-1">
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 min-h-[60px]">
                    {d.blurb}
                  </p>

                  <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {d.state}</span>
                    <span>·</span>
                    <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {d.duration}</span>
                  </div>

                  <div className="mt-5 pt-5 border-t border-border flex items-end justify-between gap-3">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Starting from</div>
                      <div className="font-display text-xl leading-tight">
                        ₹{(d.price || 0).toLocaleString("en-IN")}
                        <span className="text-xs text-muted-foreground font-body"> /person</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-2">
                    <Link to={`/booking/${d.packageIds?.[0] || "meg-3"}`} className="contents">
                      <Button data-testid={`book-${d.slug}`} className="rounded-full w-full" size="sm">Book Now</Button>
                    </Link>
                    <Link to={`/destinations/${d.slug}`} className="contents">
                      <Button data-testid={`explore-${d.slug}`} variant="outline" className="rounded-full w-full" size="sm">
                        Explore <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
